import { useFixturesStandardized } from '@/hooks/entities/useFixturesStandardized';
import { useCircuitsStandardized } from '@/hooks/entities/useCircuitsStandardized';
import { useControlsStandardized } from '@/hooks/entities/useControlsStandardized';
import { useRoomsStandardized } from '@/hooks/entities/useRoomsStandardized';
import { useLVProducts } from '@/features/product-library/hooks/useLVProducts';

type UpdateMutationParams = {
  id: string;
  updates: any;
  showSuccessToast?: boolean;
};

export function useFloorPlanMutations(floorPlanId: string) {
  // Get mutation functions from standardized hooks
  const { updateFixture, deleteFixture, assignToCircuit, updateWattage } =
    useFixturesStandardized(floorPlanId);

  const { createCircuit, updateCircuit, deleteCircuit } = useCircuitsStandardized(floorPlanId);

  const { updateControl, deleteControl } = useControlsStandardized(floorPlanId);

  const { updateRoom, deleteRoom } = useRoomsStandardized(floorPlanId);

  const { createProduct, updateProduct, deleteProduct } = useLVProducts(floorPlanId);

  const updateFixtureWithOptions = ({ id, updates, showSuccessToast }: UpdateMutationParams) => {
    if (showSuccessToast === undefined) {
      return updateFixture(id, updates);
    }

    return updateFixture(id, updates, { showSuccessToast });
  };

  const updateControlWithOptions = ({ id, updates, showSuccessToast }: UpdateMutationParams) => {
    if (showSuccessToast === undefined) {
      return updateControl(id, updates);
    }

    return updateControl(id, updates, { showSuccessToast });
  };

  // Wrapper functions that match the expected interface
  const updateFixtureMutation = {
    mutate: updateFixtureWithOptions,
    mutateAsync: updateFixtureWithOptions,
  };

  const deleteFixtureMutation = {
    mutate: (id: string) => deleteFixture(id),
    mutateAsync: (id: string) => deleteFixture(id),
  };

  const updateControlMutation = {
    mutate: updateControlWithOptions,
    mutateAsync: updateControlWithOptions,
  };

  const updateRoomMutation = {
    mutate: ({ id, updates }: { id: string; updates: any }) => updateRoom(id, updates),
    mutateAsync: ({ id, updates }: { id: string; updates: any }) => updateRoom(id, updates),
  };

  const deleteRoomMutation = {
    mutate: (id: string) => deleteRoom(id),
    mutateAsync: (id: string) => deleteRoom(id),
  };

  const createCircuitMutation = {
    mutate: ({ name, fixtureIds }: { name: string; fixtureIds: string[] }) =>
      createCircuit({ name, fixture_ids: fixtureIds, floor_plan_id: floorPlanId }),
    mutateAsync: ({ name, fixtureIds }: { name: string; fixtureIds: string[] }) =>
      createCircuit({ name, fixture_ids: fixtureIds, floor_plan_id: floorPlanId }),
  };

  // Product mutations wrapper
  const createProductMutation = {
    mutate: (productData: any) => createProduct(productData),
    mutateAsync: (productData: any) => createProduct(productData),
  };

  const updateProductMutation = {
    mutate: ({ id, updates }: { id: string; updates: any }) => updateProduct({ id, updates }),
    mutateAsync: ({ id, updates }: { id: string; updates: any }) => updateProduct({ id, updates }),
  };

  const deleteProductMutation = {
    mutate: (id: string) => deleteProduct(id),
    mutateAsync: (id: string) => deleteProduct(id),
  };

  return {
    updateFixtureMutation,
    deleteFixtureMutation,
    updateControlMutation,
    updateRoomMutation,
    deleteRoomMutation,
    createCircuitMutation,
    createProductMutation,
    updateProductMutation,
    deleteProductMutation,
    // Additional helpers
    assignFixturesToCircuit: assignToCircuit,
    updateFixtureWattage: updateWattage,
  };
}
